/**
 * Compteur de visites maison de l'app (migrations 0523, 0692) : pages vues et
 * événements de démo, envoyés à la RPC `track_event_v2`. Aucune donnée
 * personnelle (chemin nettoyé + identifiant de l'onglet + visiteur du jour) ;
 * le serveur reconnaît les robots ; si l'utilisateur est
 * connecté, le serveur pose son id pour exclure les comptes internes du récap.
 * Au mieux : une erreur n'a jamais d'effet sur l'app.
 */

import { Capacitor } from '@capacitor/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  TRACK_RPC,
  adoptVisitorParam,
  isAutomatedBrowser,
  trackEventArgs,
  trackSessionId,
  trackVisitorId,
  type TrackKind,
  type TrackPlatform,
} from '@jeraisonne/db/analytics';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export function trackPlatform(): TrackPlatform {
  try {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  } catch {
    return 'web';
  }
}

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function dailyStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function trackAppEvent(kind: TrackKind, path?: string): void {
  if (typeof window === 'undefined') return;
  // L'app lancée en local (`vite dev`) écrit dans la base de production : on ne
  // compte pas ces visites de développement.
  if (import.meta.env.MODE === 'development') return;
  try {
    const args = trackEventArgs(
      'app',
      trackPlatform(),
      kind,
      path ?? window.location.pathname,
      trackSessionId(sessionStore()),
      trackVisitorId(dailyStore()),
      isAutomatedBrowser(navigator),
    );
    void db.rpc(TRACK_RPC, args).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    // jamais bloquant
  }
}

/**
 * Visiteur venu du site (`?jv=`, migration 0718) : l'app reprend son
 * identifiant du jour et retire le paramètre de l'adresse. Avant le 1er rendu.
 */
export function adoptSiteVisitor(): void {
  if (typeof window === 'undefined') return;
  try {
    const clean = adoptVisitorParam(window.location.href, dailyStore());
    if (clean) window.history.replaceState(window.history.state, '', clean);
  } catch {
    // jamais bloquant
  }
}

/**
 * Défi reçu ouvert (page virtuelle des tunnels « défis », 0724). Le paramètre
 * qui le signale (`?defi=`, `?from=challenge`) est retiré du chemin enregistré.
 */
export type ChallengeMark = 'skillcard' | 'social';

export function markChallengeOpened(kind: ChallengeMark): void {
  trackAppEvent('page_view', `/defi-recu/${kind}`);
}

/** Lien de l'app ouvert depuis un défi partagé du site (`?from=challenge`). */
export function challengeArrivalOf(href: string): ChallengeMark | null {
  try {
    return new URL(href).searchParams.get('from') === 'challenge' ? 'social' : null;
  } catch {
    return null;
  }
}

/** Au démarrage : arrivée depuis un défi partagé du site (avant toute redirection). */
export function markChallengeArrival(): void {
  if (typeof window === 'undefined') return;
  const kind = challengeArrivalOf(window.location.href);
  if (kind) markChallengeOpened(kind);
}

/** Contenu bloqué vu (page virtuelle des tunnels de conversion, 0718). */
export type PaywallMark = 'exercice' | 'skillcard' | 'objectif' | 'epreuve' | 'prof';

export function markPaywallSeen(kind: PaywallMark): void {
  trackAppEvent('page_view', `/blocage/${kind}`);
}

/**
 * Paiement lancé (page virtuelle des tunnels de conversion, 0718). Noms sans
 * chiffre : un segment long avec un chiffre serait pris pour un identifiant.
 */
export type CheckoutMark =
  | 'pass-jour'
  | 'abonnement-semaine'
  | 'abonnement-mois'
  | 'epreuves-jour'
  | 'epreuves-semaine'
  | 'epreuves-mois'
  | 'abonnement-eleve'
  | 'abonnement-famille'
  | 'abonnement-prof'
  | 'skillcards';

export function markCheckoutStarted(product: CheckoutMark): void {
  trackAppEvent('page_view', `/paiement/${product}`);
}

/** Paiement lancé pour un pass (type × durée). */
export function passCheckoutMark(
  type: 'normal' | 'epreuves',
  duration: '24h' | '7d' | '30d',
): CheckoutMark {
  if (type === 'epreuves') {
    return duration === '24h'
      ? 'epreuves-jour'
      : duration === '7d'
        ? 'epreuves-semaine'
        : 'epreuves-mois';
  }
  return duration === '24h'
    ? 'pass-jour'
    : duration === '7d'
      ? 'abonnement-semaine'
      : 'abonnement-mois';
}
